import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronLeft,
  ExternalLink,
  GitBranch,
  Layers,
  Database,
  ListTree,
  Globe,
  Languages,
  Calendar,
  Hash,
  AlertTriangle,
  Lock,
  Workflow,
  Mic,
  Volume2,
  KeyRound,
  Cloud,
  Check,
  X,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useApi } from "@/lib/api";
import { PageShell, SectionCard } from "@/components/page";
import { cn } from "@/lib/utils";
import fingerprintsRaw from "@/data/tenant-fingerprints.json";

// ---------- Types ----------

type StatusKey =
  | "success"
  | "failure"
  | "canceled"
  | "queued"
  | "active"
  | "unknown";

type RecentDeployment = {
  id: string;
  short_id: string;
  created_on: string;
  status: StatusKey;
  branch: string;
  commit_message: string | null;
  aliases: string[];
};

type VoiceBackend = "kokoro" | "openai" | "misconfigured";

type VoiceConfig = {
  backend: VoiceBackend;
  tts_worker_url: string | null;
  tts_worker_key_set: boolean;
  use_openai_tts: string | null;
  openai_api_key_set: boolean;
  voice_api_key_set: boolean;
  voice_public_url: string | null;
};

type UserDetail = {
  id: string;
  name: string;
  persona: string;
  subdomain: string;
  url: string;
  created_on: string;
  production_branch: string;
  framework: string | null;
  last_deployment_at: string | null;
  last_deployment_status: StatusKey | null;
  latest_deployment_id: string | null;
  recent_deployments: RecentDeployment[];
  voice: VoiceConfig;
};

const VOICE_PILL: Record<VoiceBackend, { label: string; classes: string }> = {
  kokoro:        { label: "Kokoro",        classes: "bg-emerald-500/15 text-emerald-700 border-emerald-300/40" },
  openai:        { label: "OpenAI",        classes: "bg-red-500/15 text-red-700 border-red-300/40" },
  misconfigured: { label: "Misconfigured", classes: "bg-amber-500/15 text-amber-700 border-amber-300/40" },
};

const VOICE_BLURB: Record<VoiceBackend, string> = {
  kokoro:        "Voice synth runs on the self-hosted Kokoro container behind my-jarvis-tts-worker. Pages function calls TTS_WORKER_URL/synthesize with the bearer key.",
  openai:        "Tenant has no TTS_WORKER bindings — voice would hit OpenAI tts-1, but the OpenAI key is exhausted. Run the Kokoro migration on this tenant.",
  misconfigured: "Partial Kokoro setup. Either TTS_WORKER_URL or TTS_WORKER_KEY is missing, or USE_OPENAI_TTS is forcibly set to '1'. Review the env vars.",
};

type LineageHealth = "aligned" | "drifted" | "different-lineage";
type Lineage = "template" | "my-jarvis-os";

type TenantFingerprint = {
  persona: string;
  display_name: string;
  subdomain: string;
  domain: string;
  language: string;
  lineage: Lineage;
  lineage_health: LineageHealth;
  deps_count: number;
  deps_delta_from_template: number;
  db_drivers: string[];
  pages_functions_count: number;
  pages_functions: string[];
  atomic_crm_folder_count: number;
  sidebar_items_count: number;
  sidebar_items: string[];
  diagram?: string;
  verdict: string;
  drift_notes: string[];
};

type FingerprintsFile = {
  tenants: Record<string, TenantFingerprint>;
};

const fingerprints = fingerprintsRaw as unknown as FingerprintsFile;

// ---------- Visual constants (mirrored from UsersPage) ----------

const AVATAR_PALETTE: { bg: string; fg: string }[] = [
  { bg: "bg-blue-500/15", fg: "text-blue-700" },
  { bg: "bg-emerald-500/15", fg: "text-emerald-700" },
  { bg: "bg-violet-500/15", fg: "text-violet-700" },
  { bg: "bg-amber-500/15", fg: "text-amber-700" },
  { bg: "bg-rose-500/15", fg: "text-rose-700" },
  { bg: "bg-teal-500/15", fg: "text-teal-700" },
];

function avatarTone(persona: string): { bg: string; fg: string } {
  const ch = persona.charCodeAt(0) || 0;
  return AVATAR_PALETTE[ch % AVATAR_PALETTE.length];
}

const STATUS_DOT: Record<StatusKey, string> = {
  success: "bg-emerald-500",
  failure: "bg-red-500",
  canceled: "bg-slate-400",
  queued: "bg-amber-500",
  active: "bg-amber-500",
  unknown: "bg-slate-400",
};

const STATUS_LABEL: Record<StatusKey, string> = {
  success: "Live",
  failure: "Failed",
  canceled: "Canceled",
  queued: "Queued",
  active: "Building",
  unknown: "Unknown",
};

// ---------- Health pill ----------

type HealthState = {
  label: string;
  classes: string;
};

function healthForLineage(h: LineageHealth | null): HealthState {
  switch (h) {
    case "aligned":
      return {
        label: "Healthy",
        classes:
          "bg-emerald-500/15 text-emerald-700 border-emerald-300/40",
      };
    case "drifted":
      return {
        label: "Watching",
        classes: "bg-amber-500/15 text-amber-700 border-amber-300/40",
      };
    case "different-lineage":
      return {
        label: "At risk",
        classes: "bg-red-500/15 text-red-700 border-red-300/40",
      };
    default:
      return {
        label: "Unknown",
        classes: "bg-slate-500/10 text-slate-700 border-slate-300/40",
      };
  }
}

function lineageBadge(lineage: Lineage): {
  label: string;
  classes: string;
} {
  if (lineage === "template") {
    return {
      label: "template",
      classes: "bg-emerald-500/15 text-emerald-700 border-emerald-300/40",
    };
  }
  return {
    label: "my-jarvis-os",
    classes: "bg-red-500/15 text-red-700 border-red-300/40",
  };
}

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return formatDistanceToNow(d, { addSuffix: true });
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return `${s.slice(0, n - 1)}…`;
}

function depsDeltaTone(delta: number): string {
  if (delta > 20) return "text-red-700";
  return "text-foreground";
}

// ---------- Page ----------

export function UserDetailPage() {
  const { persona = "" } = useParams<{ persona: string }>();
  const api = useApi();
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fingerprint =
    persona && fingerprints.tenants[persona]
      ? fingerprints.tenants[persona]
      : null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!persona) return;
      setLoading(true);
      setError(null);
      try {
        const res = await api(`/api/users/${persona}`);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`HTTP ${res.status} — ${text || "request failed"}`);
        }
        const data = (await res.json()) as UserDetail;
        if (!cancelled) setDetail(data);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona]);

  if (loading && !detail) {
    return (
      <PageShell title="Loading…" subtitle="Fetching tenant detail" iconColor="violet">
        <SectionCard>
          <p className="text-sm text-muted-foreground">
            Loading tenant {persona}…
          </p>
        </SectionCard>
      </PageShell>
    );
  }

  if (error || !detail) {
    return (
      <PageShell title="Tenant not found" subtitle={persona} iconColor="rose">
        <SectionCard>
          <p className="text-sm text-muted-foreground">
            {error ?? "No data returned for this tenant."}
          </p>
          <Link
            to="/users"
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Users
          </Link>
        </SectionCard>
      </PageShell>
    );
  }

  const tone = avatarTone(detail.persona);
  const health = healthForLineage(fingerprint?.lineage_health ?? null);
  const displayName = fingerprint?.display_name ?? detail.persona;

  // ---------- Risk signals ----------
  const now = Date.now();
  const lastDeployTs = detail.last_deployment_at
    ? new Date(detail.last_deployment_at).getTime()
    : null;
  const noDeploy14d =
    lastDeployTs !== null && now - lastDeployTs > 14 * 24 * 60 * 60 * 1000;
  const failedLastBuild =
    detail.recent_deployments[0]?.status === "failure";
  const staleLineage = fingerprint?.lineage === "my-jarvis-os";
  const heavyDrift = (fingerprint?.deps_delta_from_template ?? 0) > 30;
  const mixedDb = (fingerprint?.db_drivers.length ?? 0) > 1;

  const signals: { label: string; fired: boolean }[] = [
    { label: "No deploy in 14d", fired: noDeploy14d },
    { label: "Failed last build", fired: failedLastBuild },
    { label: "Stale lineage", fired: !!staleLineage },
    { label: "Heavy drift", fired: heavyDrift },
    { label: "Mixed DB", fired: mixedDb },
  ];

  return (
    <PageShell title={displayName} subtitle="Tenant detail" iconColor="violet">
      {/* Section A — Header strip */}
      <SectionCard>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <Link
              to="/users"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mt-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Users
            </Link>

            <span
              className={cn(
                "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                tone.bg,
                tone.fg,
              )}
              aria-hidden
            >
              {(detail.persona[0] ?? "?").toUpperCase()}
            </span>

            <div className="min-w-0">
              <h2 className="text-lg font-semibold capitalize text-foreground truncate">
                {detail.persona}
              </h2>
              <a
                href={detail.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline"
              >
                {detail.subdomain}
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>

          <span
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
              health.classes,
            )}
          >
            {health.label}
          </span>
        </div>
      </SectionCard>

      {/* Section B — Identity card */}
      <SectionCard eyebrow="Identity">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          <IdentityRow
            icon={Globe}
            label="Live URL"
            value={
              <a
                href={detail.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-blue-600 hover:underline break-all"
              >
                {detail.url}
                <ExternalLink className="h-3 w-3 shrink-0" />
              </a>
            }
          />
          <IdentityRow
            icon={Hash}
            label="Subdomain"
            value={
              <span className="font-mono text-xs text-muted-foreground break-all">
                {detail.subdomain}
              </span>
            }
          />
          <IdentityRow
            icon={Layers}
            label="Framework"
            value={
              <span className="text-sm text-foreground">
                {detail.framework ?? "—"}
              </span>
            }
          />
          <IdentityRow
            icon={GitBranch}
            label="Production branch"
            value={
              <span className="font-mono text-xs text-muted-foreground">
                {detail.production_branch}
              </span>
            }
          />
          <IdentityRow
            icon={Hash}
            label="Project ID"
            value={
              <span className="font-mono text-xs text-muted-foreground break-all">
                {detail.id}
              </span>
            }
          />
          <IdentityRow
            icon={Calendar}
            label="Created"
            value={
              <span className="text-sm text-foreground">
                {new Date(detail.created_on).toLocaleDateString()}{" "}
                <span className="text-xs text-muted-foreground">
                  ({relativeTime(detail.created_on)})
                </span>
              </span>
            }
          />
        </dl>
      </SectionCard>

      {/* Section C — Architecture fingerprint */}
      <SectionCard eyebrow="Architecture fingerprint">
        {fingerprint ? (
          <FingerprintBody fp={fingerprint} />
        ) : (
          <p className="text-sm text-muted-foreground italic">
            No fingerprint captured for this tenant yet.
          </p>
        )}
      </SectionCard>

      {/* Section C2 — Voice configuration */}
      <SectionCard eyebrow="Voice configuration">
        <VoiceBody voice={detail.voice} />
      </SectionCard>

      {/* Section D — Work-process diagram */}
      <SectionCard eyebrow="Work process">
        {fingerprint?.diagram ? (
          <div className="flex items-start gap-3">
            <Workflow className="h-4 w-4 mt-1 shrink-0 text-muted-foreground" />
            <img
              src={fingerprint.diagram}
              alt={`${displayName} work process diagram`}
              className="rounded-lg border w-full"
            />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">
            No diagram available.
          </p>
        )}
      </SectionCard>

      {/* Section E — Deployment activity */}
      <SectionCard eyebrow="Deployment activity">
        {detail.recent_deployments.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            No deployments found.
          </p>
        ) : (
          <ol className="space-y-3">
            {detail.recent_deployments.map((d) => (
              <li
                key={d.id || d.short_id}
                className="grid grid-cols-[10px_1fr_auto] items-start gap-3"
              >
                <span
                  className={cn(
                    "mt-1.5 h-2 w-2 rounded-full",
                    STATUS_DOT[d.status],
                  )}
                  aria-hidden
                />
                <div className="min-w-0">
                  <p className="text-sm text-foreground">
                    <span className="font-medium">
                      {STATUS_LABEL[d.status]}
                    </span>
                    <span className="text-muted-foreground">
                      {" · "}
                      {d.branch || "—"}
                    </span>
                    {d.commit_message ? (
                      <>
                        <span className="text-muted-foreground"> · </span>
                        <span className="text-muted-foreground">
                          {truncate(d.commit_message.split("\n")[0], 80)}
                        </span>
                      </>
                    ) : null}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {relativeTime(d.created_on)}
                  </p>
                </div>
                <span className="font-mono text-[11px] text-muted-foreground">
                  {d.short_id}
                </span>
              </li>
            ))}
          </ol>
        )}
      </SectionCard>

      {/* Section F — Risk signals */}
      <SectionCard eyebrow="Risk signals">
        <div className="flex flex-wrap gap-2">
          {signals.map((s) => (
            <RiskChip key={s.label} label={s.label} fired={s.fired} />
          ))}
        </div>
      </SectionCard>

      {/* Section G — Stub / future sections */}
      <SectionCard eyebrow="Coming once usage data is wired" variant="muted">
        <ul className="space-y-1.5 text-sm text-muted-foreground">
          <li>· Engagement sparklines (DAU/WAU + key actions)</li>
          <li>· Day×hour behavioral heatmap</li>
          <li>· Activation funnel</li>
          <li>· Top features adopted</li>
          <li>· Self-vs-self trend</li>
        </ul>
        <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <Lock className="h-3 w-3" />
          Requires tenant consent for usage data
        </p>
      </SectionCard>
    </PageShell>
  );
}

// ---------- Sub-components ----------

function IdentityRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Globe;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 min-w-0">
      <Icon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </dt>
        <dd className="mt-0.5 min-w-0">{value}</dd>
      </div>
    </div>
  );
}

function FingerprintBody({ fp }: { fp: TenantFingerprint }) {
  const lineage = lineageBadge(fp.lineage);
  const delta = fp.deps_delta_from_template;
  const deltaLabel =
    delta === 0 ? "+0" : delta > 0 ? `+${delta}` : `${delta}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
            lineage.classes,
          )}
        >
          Lineage: {lineage.label}
        </span>
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-xs",
            depsDeltaTone(delta),
          )}
        >
          <Layers className="h-3 w-3" />
          {fp.deps_count} deps ({deltaLabel})
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-xs text-foreground">
          <ListTree className="h-3 w-3" />
          {fp.pages_functions_count} endpoints
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-xs text-foreground">
          <Layers className="h-3 w-3" />
          {fp.sidebar_items_count} sidebar items
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          DB drivers:
        </span>
        {fp.db_drivers.length === 0 ? (
          <span className="text-xs text-muted-foreground italic">none</span>
        ) : (
          fp.db_drivers.map((db) => (
            <span
              key={db}
              className="inline-flex items-center gap-1 rounded-full border bg-card px-2 py-0.5 text-xs"
            >
              <Database className="h-3 w-3" />
              {db}
            </span>
          ))
        )}
      </div>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 text-sm">
        <div className="flex items-start gap-2">
          <Globe className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Domain
            </dt>
            <dd className="text-foreground">{fp.domain}</dd>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Languages className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Language
            </dt>
            <dd className="text-foreground">{fp.language}</dd>
          </div>
        </div>
      </dl>

      {fp.verdict ? (
        <p className="text-sm italic text-muted-foreground">{fp.verdict}</p>
      ) : null}

      {fp.drift_notes.length > 0 ? (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
            Drift notes
          </p>
          <ul className="space-y-1.5 text-sm text-foreground">
            {fp.drift_notes.map((note, i) => (
              <li key={i} className="flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-1 shrink-0 text-amber-600" />
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function VoiceBody({ voice }: { voice: VoiceConfig }) {
  const pill = VOICE_PILL[voice.backend];
  const blurb = VOICE_BLURB[voice.backend];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
            pill.classes,
          )}
        >
          <Mic className="h-3 w-3" />
          Backend: {pill.label}
        </span>
        {voice.backend === "kokoro" ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-xs text-foreground">
            <Volume2 className="h-3 w-3" />
            Self-hosted on Cloudflare
          </span>
        ) : null}
      </div>

      <p className="text-sm italic text-muted-foreground">{blurb}</p>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        <VoiceRow
          icon={Cloud}
          label="TTS_WORKER_URL"
          value={
            voice.tts_worker_url ? (
              <a
                href={voice.tts_worker_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-xs text-blue-600 hover:underline break-all"
              >
                {voice.tts_worker_url}
              </a>
            ) : (
              <Absent />
            )
          }
        />
        <VoiceRow
          icon={KeyRound}
          label="TTS_WORKER_KEY"
          value={voice.tts_worker_key_set ? <Present text="secret set" /> : <Absent />}
        />
        <VoiceRow
          icon={Hash}
          label="USE_OPENAI_TTS"
          value={
            voice.use_openai_tts !== null ? (
              <span className="font-mono text-xs text-foreground">
                {voice.use_openai_tts === "0" ? '"0" (Kokoro)' : `"${voice.use_openai_tts}" (OpenAI fallback)`}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground italic">unset (defaults to Kokoro)</span>
            )
          }
        />
        <VoiceRow
          icon={KeyRound}
          label="OPENAI_API_KEY"
          value={
            voice.openai_api_key_set ? (
              <span className="text-xs text-muted-foreground">
                <Present text="legacy secret still on project" />
              </span>
            ) : (
              <span className="text-xs text-muted-foreground italic">unset (clean — voice no longer uses OpenAI)</span>
            )
          }
        />
        <VoiceRow
          icon={KeyRound}
          label="VOICE_API_KEY"
          value={voice.voice_api_key_set ? <Present text="MCP bearer set" /> : <Absent />}
        />
        <VoiceRow
          icon={Globe}
          label="VOICE_PUBLIC_URL (R2)"
          value={
            voice.voice_public_url ? (
              <a
                href={voice.voice_public_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-xs text-blue-600 hover:underline break-all"
              >
                {voice.voice_public_url}
              </a>
            ) : (
              <Absent />
            )
          }
        />
      </dl>
    </div>
  );
}

function VoiceRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Globe;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 min-w-0">
      <Icon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </dt>
        <dd className="mt-0.5 min-w-0">{value}</dd>
      </div>
    </div>
  );
}

function Present({ text }: { text: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
      <Check className="h-3 w-3" />
      {text}
    </span>
  );
}

function Absent() {
  return (
    <span className="inline-flex items-center gap-1 text-xs text-red-700">
      <X className="h-3 w-3" />
      not set
    </span>
  );
}

function RiskChip({ label, fired }: { label: string; fired: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
        fired
          ? "bg-red-500/15 text-red-700 border-red-300/40"
          : "bg-muted/40 text-muted-foreground border-border",
      )}
    >
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          fired ? "bg-red-500" : "bg-muted-foreground/40",
        )}
        aria-hidden
      />
      {label}
    </span>
  );
}
